import React, { useState, useEffect, useRef, createRef } from "react";
import styles from "./ComponentB.module.css";

const ComponentB = ({ guardsData, onSelectGuard, setSelectedGuard, carStatus, updateCarStatus, setRefresh }) => {
  const [selectedGuardId, setSelectedGuardId] = useState(null); // Track selected guard by ID
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const rowRefs = useRef([]); // Create a ref object to store refs for all rows
  const scrollRef = useRef(null); //scroll position fix

  useEffect(() => {
    const el = scrollRef.current;

    const handleBeforeUnload = () => {
      if (el) {
        localStorage.setItem("scrollLeft_itemB", el.scrollLeft);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    const savedScroll = localStorage.getItem("scrollLeft_itemB");

    if (el && savedScroll !== null) {
      // Wait until next animation frame to ensure layout is ready
      requestAnimationFrame(() => {
        el.scrollLeft = parseInt(savedScroll, 10);
      });
    }
  }, []);

  // Adjust rowRefs to hold a ref for each row
  useEffect(() => {
    rowRefs.current = guardsData.map((_, i) => rowRefs.current[i] || createRef());
  }, [guardsData]);

  useEffect(() => {
    //console.log("setRefresh(10000);  ");
    setRefresh(10000);
  });

  const toggleCarStatus = (serial) => {
    const currentStatus = carStatus[serial] || "Speed off";
    const nextStatus = {
      "Speed off": "Speed on",
      "Speed on": "Car off",
      "Car off": "Speed off",
    };
    updateCarStatus(serial, nextStatus[currentStatus]);
  };

  const handleRowClick = (guard) => {
    setSelectedGuard(guard);
    setSelectedGuardId(guard.serial); // Track selected guard by ID
  };

  const getRowClassName = (guard) => {
    if (guard.color === "red") return styles.backgroundRed;
    if (guard.color === "orange") return styles.backgroundOrange;
    if (guard.color === "yellow") return styles.backgroundYellow;
    if (guard.color === "green") return styles.backgroundGreen;
    if (guard.color === "blue") return styles.backgroundBlue;
    if (guard.color === "brown") return styles.backgroundBrown;
    if (guard.color === "lightblue") return styles.backgroundLightblue;
    return styles.backgroundGrey;
  };

  const getMsgClassName = (guard) => {
    return guard.IncMSG ? styles.msgPresent : "";
  };

  const sortedGuardsData = React.useMemo(() => {
    let sortableItems = [...guardsData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [guardsData, sortConfig]);

  // This useEffect depends on sortedGuardsData, so it must be defined after sortedGuardsData
  useEffect(() => {
    if (selectedGuardId !== null) {
      const selectedIndex = sortedGuardsData.findIndex((guard) => guard.serial === selectedGuardId);
      if (selectedIndex !== -1 && rowRefs.current[selectedIndex]) {
        rowRefs.current[selectedIndex].current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    }
  }, [selectedGuardId, sortedGuardsData]);

  useEffect(() => {
    if (selectedGuardId !== null && guardsData.length > 0) {
      const selectedGuard = guardsData.find((guard) => guard.serial === selectedGuardId);
      if (selectedGuard) {
        setSelectedGuard(selectedGuard);
        console.log("setSelectedGuard(selectedGuard) ", selectedGuard);
      } else {
        setSelectedGuard(null);
        setSelectedGuardId(null);
      }
    }
  }, [guardsData, selectedGuardId, setSelectedGuard]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const excludedKeys = ["carNo", "title", "speed", "serial", "validity", "HOP", "heading"];

  const columnLabels = {
    lat: "Lat",
    lon: "Lon",
    bat: "Batt",
    alt: "Alt",
    sat: "Sat",
    gpstime: "GPS Time",
  };

  return (
    <div className={styles.tableContainerB}>
      <table className={styles.tableB}>
        <thead>
          <tr className={styles.fixedHeader}>
            {guardsData.length > 0 && (
              <>
                <th onClick={() => requestSort("status")}>Status</th>
                <th onClick={() => requestSort("carNo")}>Car No</th>
                <th onClick={() => requestSort("title")}>Capacity</th>
                <th onClick={() => requestSort("speed")}>
                  Speed <br /> Km/h
                </th>
                {"NAME" in guardsData[0] && <th onClick={() => requestSort("NAME")}>Name</th>}
                <th onClick={() => requestSort("serial")}>S/N</th>
                {Object.keys(guardsData[0])
                  .filter((key) => !excludedKeys.includes(key))
                  .map((key) => (
                    <th key={key} onClick={() => requestSort(key)}>
                      {columnLabels[key] || key}
                    </th>
                  ))}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedGuardsData.map((guard, idx) => (
            <tr
              key={guard.serial}
              ref={rowRefs.current[idx]}
              className={selectedGuardId === guard.serial ? `${styles.selectedRow} ${getRowClassName(guard)} ${getMsgClassName(guard)}` : `${styles.normalRow} ${getRowClassName(guard)} ${getMsgClassName(guard)}`}
              onClick={() => handleRowClick(guard)}>
              <td>
                <button onClick={() => toggleCarStatus(guard.serial)}>{carStatus[guard.serial] || "Speed off"}</button>
              </td>
              <td>{guard.carNo}</td>
              <td style={{ backgroundColor: guard.color || "transparent", fontWeight: "bold" }}>{guard.title}</td>
              <td>{guard.speed}</td>
              

              {/* ✅ Conditionally render "NAME" column */}
              {"NAME" in guard && <td>{guard.NAME}</td>}
              <td>{guard.serial}</td>

              {/* ✅ Remaining dynamic fields */}
              {Object.entries(guard)
                .filter(([key]) => !excludedKeys.includes(key) && key !== "NAME")
                .map(([key, value]) => (
                  <td key={key}>{value}</td>
                ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ComponentB;
